import { useEffect, useMemo, useState } from 'react';
import _, { defaultTo, uniq, xor } from 'lodash';
import { useFormik } from 'formik';
import { SelectOptions } from 'components/commonType';

import { DefaultAdRequestSourceManager } from 'core/adRequestSource/AdRequestSourceManager';
import { DefaultBrandManager } from 'core/brand/BrandManager';
import { DefaultRdpManager, RdpManager } from 'core/rdp/RdpManager';
import { useCallAPI } from 'hooks/useCallAPI';
import { useCampaignStore } from 'store/campaignStore';
import { useOrderStore } from 'store/orderStore';
import { useProductStore } from 'store/productStore';
import {
  BidMode,
  RoasStrategy,
  SuggestKeywordProperty,
  SuggestKeywordType,
} from 'core/rtbCampaign/singlePage/keyword';
import {
  SelectPattern,
  BudgetType,
  ActionType,
} from 'core/rtbCampaign/singlePage/common';
import { Product, ProductWithPagination } from 'core/product/Product';
import moment from 'moment';
import useAlwaysOnBudgetManagement from './useAlwaysOnBudgetManagement';
import useDateManaget from './useDateManagement';
import useRetailerManagement from './useRetailerManagement';

interface KeywordForm {
  defaultForm: any;
  values: ReturnType<typeof useFormik>['values'];
  setFieldValue: ReturnType<typeof useFormik>['setFieldValue'];
}

export enum Goal {
  LINK_CLICKS = 'LINK_CLICKS',
  CONVERSIONS = 'CONVERSIONS',
}

export interface OutboundInfo {
  brandNo: string;
  brandName: string;
  name: string;
  logoUrl: string;
  adContent: string;
  segment: string[];
  goal: Goal | '';
}

const defaultBrandManager = new DefaultBrandManager();
const defaultRdpManager: RdpManager = new DefaultRdpManager();
const adRequestSourceManager = new DefaultAdRequestSourceManager();

/**
 *
 * @param initialState
 * @returns
 *
 */
const useKeywordForm = (
  initialState: KeywordForm = {
    defaultForm: {},
    values: {},
    setFieldValue: async () => Promise.resolve(),
  },
) => {
  const { defaultForm, values, setFieldValue } = initialState;

  const { callAPIs } = useCallAPI();

  const { order } = useOrderStore();

  const { actionType, showProductManager, handleLoading, handleSetRoasList } =
    useCampaignStore();

  const {
    liveProducts,
    selectedAutoProducts,
    selectedProducts,
    changeSelectedProducts,
    changeSelectedAutoProducts,
    handleSetProductList,
  } = useProductStore();

  // 站內品牌清單
  const [brandOptions, setBrandOptions] = useState<SelectOptions[]>([]);

  // 手動關鍵字
  const [suggestKeywords, setSuggestKeywords] = useState<SuggestKeywordType[]>(
    [],
  );
  // 活動期間
  const [diffDays, setDiffDays] = useState(1);

  // 站內已選品牌
  const [selectBrand, setSelectBrand] = useState<string | null>(
    values.basic.webBrandNo ? values.basic.webBrandNo : '1',
  );

  // 確認是否更新手動商品
  const needUpdateProducts = useMemo(() => {
    const oriProducts = defaultTo(defaultForm.products, []);
    const oriProductIds = uniq(oriProducts.map((p) => p.productId));
    const selectedProductIds = uniq(selectedProducts?.map((p) => p.productId));
    return xor(oriProductIds, selectedProductIds).length > 0;
  }, [defaultForm?.products, selectedProducts]);

  /* 站內 */

  // 廣告主管理，取得廣告主資訊
  const { filters, retailerTypeOptions } = useRetailerManagement({
    defaultRdpManager,
  });

  // 預算管理
  const {
    handleBudgetTypeChange,
    handleAlwaysOnBudgetChange,
    handleAlwaysOnAutoSetBudget,
    handleResetAllBudget,
  } = useAlwaysOnBudgetManagement({
    defaultForm,
    values,
    setFieldValue,
  });

  // 日期管理
  const {
    handleDurationChange,
    handleDateRangeChange,
    handleChangeStartDate,
    handleChangeEndDate,
  } = useDateManaget({
    defaultForm,
    values,
    setFieldValue,
    handleResetAllBudget,
  });

  // 取得站內品牌選項
  const fetchBrandOptions = async (): Promise<void> => {
    try {
      await callAPIs(
        [defaultBrandManager.getMaxCvrBrandOptions.bind(defaultBrandManager)],
        (brandOptions) => {
          if (
            brandOptions.some(({ value }) => value === values.basic.webBrandNo)
          ) {
            values.basic.brandName = brandOptions.filter(
              ({ value }) => value === values.basic.webBrandNo,
            )[0].label;
            setSelectBrand(values.basic.webBrandNo);
          }
          // 必須過濾掉"其他"品牌
          const preprocessBrandOptions = brandOptions.filter(
            ({ value }) => value !== '99999999999999999',
          );
          const options = [
            { value: '1', label: '全部' },
            ...preprocessBrandOptions,
          ];
          setBrandOptions(options);
        },
      );
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      handleLoading(false);
    }
  };

  // 根據已選擇的品牌取得商品
  const fetchBrandProducts = async (
    brandName: string | undefined | null,
  ): Promise<Product[] | undefined> => {
    if (!_.isEmpty(filters) && !_.isNil(filters.advertiser)) {
      const retailId = _.get(filters, 'retailerId', '');
      // const queries = handleSearchQueries(filters, categories);
      if (_.isEmpty(retailId)) {
        return undefined;
      }
      try {
        const pageable = { page: 1, sizePerPage: 25 };
        changeSelectedAutoProducts(() => []);
        let products: Product[] | undefined;
        await callAPIs(
          [
            defaultRdpManager.getRetailProducts.bind(
              defaultRdpManager,
              retailId,
              pageable,
              {
                isMaxCvrSmartKeyword: true,
                brand: brandName,
              },
            ),
          ],
          (productWithPagination: ProductWithPagination) => {
            if (productWithPagination.products.length === 0) {
              setFieldValue('limitations', {
                include: [
                  {
                    op: '',
                    type: 'searchKeywords',
                    value: [],
                  },
                ],
              });
              setSuggestKeywords([]);
              if (
                actionType === ActionType.CREATE ||
                actionType === ActionType.COPY
              ) {
                setFieldValue('basic.bidMode', BidMode.MANUAL);
                setFieldValue('basic.roasOption.roasStrategy', null);
              }
              products = productWithPagination.products;
              return;
            }
            changeSelectedAutoProducts(() => productWithPagination.products);
            products = productWithPagination.products;
            // fetchKeywords(productWithPagination.products);
          },
        );
        return products;
      } catch (error) {
        console.error('Failed to fetch data:', error);
        handleLoading(false);
        return undefined;
      }
    }
    return undefined;
  };

  // 根據已選擇的品牌取得商品
  const fetchProductsToGetRoas = async (
    productIds: string[],
  ): Promise<void> => {
    if (!_.isEmpty(filters) && !_.isNil(filters.advertiser)) {
      const retailId = _.get(filters, 'retailerId', '');
      // const queries = handleSearchQueries(filters, categories);
      if (_.isEmpty(retailId)) {
        return;
      }
      try {
        const pageable = { page: 1, sizePerPage: 25 };
        handleSetProductList([]);
        await callAPIs(
          [
            defaultRdpManager.getRetailProducts.bind(
              defaultRdpManager,
              retailId,
              pageable,
              {
                productIds: productIds,
              },
            ),
          ],
          (productWithPagination: ProductWithPagination) => {
            handleSetProductList(productWithPagination.products);
          },
        );
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        handleLoading(false);
      }
    }
  };

  // 取得推薦關鍵字
  const fetchKeywords = async (products): Promise<void> => {
    try {
      const smartGoodsHotKeywords =
        await adRequestSourceManager.getSmartGoodsHotKeywords(
          defaultTo(products, []).map((product) => product.productId),
        );
      if (smartGoodsHotKeywords.suggestKeywords.length === 0) {
        console.error('No suggestKeywords');
        setFieldValue('limitations', {
          include: [
            {
              op: '',
              type: 'searchKeywords',
              value: [],
            },
          ],
        });
        setSuggestKeywords([]);
        if (
          actionType === ActionType.CREATE ||
          actionType === ActionType.COPY
        ) {
          setFieldValue('basic.bidMode', BidMode.MANUAL);
          setFieldValue('basic.roasOption.roasStrategy', null);
          setFieldValue('basic.roasOption.roas', null);
        }
        return;
      }

      const allCounts = smartGoodsHotKeywords.suggestKeywords.length;
      setSuggestKeywords(
        smartGoodsHotKeywords.suggestKeywords.map((item, i) => ({
          [SuggestKeywordProperty.SUGGEST_KEYWORD]: item.suggestKeyword,
          [SuggestKeywordProperty.PR]: item.pr?.toString(),
          [SuggestKeywordProperty.CPC]: item.cpc?.toString() || '',
          [SuggestKeywordProperty.STAR]: 5 - Math.floor((5 * i) / allCounts),
        })),
      );
      setFieldValue('limitations', {
        include: [
          {
            op: '',
            type: 'searchKeywords',
            value: smartGoodsHotKeywords.suggestKeywords.map((keyword) => ({
              value: keyword.suggestKeyword,
              label: keyword.suggestKeyword,
              isError: false,
            })),
          },
        ],
      });
      if (
        actionType === ActionType.CREATE &&
        values.basic.bidMode === BidMode.MANUAL &&
        allCounts > 0
      ) {
        setFieldValue('basic.bidMode', BidMode.AUTO);
        setFieldValue('basic.roasOption.roasStrategy', RoasStrategy.HIGH_ROAS);
        setFieldValue('basic.roasOption.roas', null);
      }
    } catch (error) {
      console.error('Failed to fetch suggestKeywords:', error);
      setFieldValue('limitations', {
        include: [
          {
            op: '',
            type: 'searchKeywords',
            value: [],
          },
        ],
      });
      setSuggestKeywords([]);
    } finally {
      handleLoading(false);
    }
  };

  // 自動選品時取得商品和關鍵字
  const fetchAutoProductAndKeywords = async (brandName?: string | null) => {
    try {
      handleLoading(true);
      const fetchedProducts = await fetchBrandProducts(brandName);
      if (fetchedProducts && fetchedProducts.length > 0) {
        await fetchKeywords(fetchedProducts);
      } else {
        handleLoading(false);
      }
    } catch (error) {
      console.error('Failed to fetch auto products and keywords:', error);
      handleLoading(false);
    }
  };

  // 切換智能選品/手動選品
  const handleSelectPatternChange = () => {
    const newSelectPattern =
      values.basic.selectPattern === SelectPattern.AUTO
        ? SelectPattern.MANUAL
        : SelectPattern.AUTO;
    setFieldValue('basic.selectPattern', newSelectPattern);
    setFieldValue('keywords', []);
    changeSelectedAutoProducts(() => []);
    changeSelectedProducts(() => []);
    if (
      actionType === ActionType.CREATE ||
      actionType === ActionType.COPY ||
      defaultForm.basic.selectPattern === SelectPattern.MANUAL
    ) {
      setFieldValue('basic.webBrandNo', null);
    }
    if (newSelectPattern === SelectPattern.AUTO) {
      if (selectBrand === '1') {
        fetchAutoProductAndKeywords(null);
      } else {
        const brandName = brandOptions.find(
          (brand) => brand.value === selectBrand,
        )?.label;
        fetchAutoProductAndKeywords(brandName);
      }
    }
  };

  // 品牌變更
  const handleBrandChange = (newBrand) => {
    const brandName = brandOptions.find(
      (brand) => brand.value === newBrand,
    )?.label;
    setSelectBrand(newBrand);
    if (newBrand === '1') {
      setFieldValue('basic.webBrandNo', null);
      setFieldValue('basic.brandName', null);
      fetchAutoProductAndKeywords(null);
    } else {
      setFieldValue('basic.webBrandNo', newBrand);
      setFieldValue('basic.brandName', brandName);
      fetchAutoProductAndKeywords(brandName);
    }
  };
  // 切換出價選擇
  const handleBidTypeChange = (newBidMode) => {
    setFieldValue('basic.bidMode', newBidMode);
    setFieldValue('basic.roasOption.roas', null);
    if (newBidMode === BidMode.MANUAL) {
      setFieldValue('basic.roasOption.roasStrategy', null);
    } else {
      setFieldValue('basic.roasOption.roasStrategy', RoasStrategy.HIGH_ROAS);
    }
  };

  // 切換ROAS策略
  const handleRoasStrategyChange = (newAutoMode) => {
    setFieldValue('basic.roasOption.roasStrategy', newAutoMode);
    setFieldValue('basic.roasOption.roas', null);
  };

  // 自動出價-自訂 ROAS 修改
  const handleCustomRoasChange = (newCustomRoas) => {
    setFieldValue('basic.roasOption.roas', newCustomRoas);
  };

  // 手動設定關鍵字
  const handleManualKeywordsChange = (newManualKeywords) => {
    setFieldValue('keywords', newManualKeywords);
  };

  // 取得 自動出價 ROAS 價格
  const handleGetRoasPrice = (products: Product[]) => {
    try {
      // ROAS目標參數
      const roasList = [0.8, 1, 1.2];
      // 根據各商品歷史ROAS*ROAS目標參數*各商品花費權重加總計算出活動層級的預估ROAS
      const finalRoasList = roasList.map((roas) => {
        const totalPrice = products.reduce((acc, { productId, spending }) => {
          const liveProduct = liveProducts?.find(
            (liveProduct) => liveProduct.productId === productId,
          );
          const liveSpending = liveProduct?.spending ?? spending;
          return acc + liveSpending;
        }, 0);
        const totalRoas = products
          .reduce((acc, { productId, customLabel4, spending }) => {
            const liveProduct = liveProducts?.find(
              (liveProduct) => liveProduct.productId === productId,
            );
            const liveRoas = liveProduct?.customLabel4 ?? customLabel4;
            const liveSpending = liveProduct?.spending ?? spending;
            const weight = liveSpending / totalPrice;
            return acc + Number(liveRoas) * roas * weight;
          }, 0)
          .toFixed(2);
        return totalRoas === '0.00' ? '-' : totalRoas;
      });
      handleSetRoasList(finalRoasList);
      return finalRoasList;
    } catch (error) {
      console.error('Error calculating ROAS price:', error);
    }
  };

  // 取得智能選品下拉選單
  useEffect(() => {
    fetchBrandOptions();
  }, []);

  useEffect(() => {
    setFieldValue('products', selectedProducts);
    setFieldValue('autoProducts', selectedAutoProducts);
  }, [selectedProducts, selectedAutoProducts, setFieldValue]);

  useEffect(() => {
    // 智能選品在新增模式初始化取得商品
    if (
      actionType === ActionType.CREATE &&
      !_.isEmpty(filters) &&
      values.basic.selectPattern === SelectPattern.AUTO &&
      selectedAutoProducts?.length === 0
    ) {
      fetchAutoProductAndKeywords(null);
    }

    // 手動選品在編輯模式初始化沒有商品取消 loading
    if (
      (actionType === ActionType.EDIT || actionType === ActionType.COPY) &&
      values.basic.selectPattern === SelectPattern.MANUAL &&
      defaultForm.products?.length === 0
    ) {
      handleLoading(false);
    }
  }, [actionType, filters]);

  useEffect(() => {
    // 智能選品在編輯模式初始化取得關鍵字
    if (
      (actionType === ActionType.EDIT || actionType === ActionType.COPY) &&
      defaultForm.basic.selectPattern === SelectPattern.AUTO
    ) {
      fetchKeywords(selectedAutoProducts);
    }
  }, [actionType, defaultForm.basic.selectPattern]);

  // 手動選品取得關鍵字
  useEffect(() => {
    const products =
      values.basic.selectPattern === SelectPattern.MANUAL &&
      !showProductManager &&
      selectedProducts?.length
        ? selectedProducts
        : null;

    if (products) {
      fetchKeywords(products);
    }
  }, [
    values.basic.selectPattern,
    selectedAutoProducts,
    selectedProducts,
    showProductManager,
  ]);

  // 手動選品異動清空手動關鍵字
  useEffect(() => {
    if (
      values.basic.selectPattern === SelectPattern.MANUAL &&
      needUpdateProducts
    ) {
      setFieldValue('keywords', []);
    }
  }, [needUpdateProducts, values.basic.selectPattern]);

  useEffect(() => {
    handleGetRoasPrice(
      values.basic.selectPattern === SelectPattern.AUTO
        ? selectedAutoProducts
        : selectedProducts,
    );
  }, [
    selectedProducts,
    selectedAutoProducts,
    values.basic.selectPattern,
    liveProducts,
  ]);

  useEffect(() => {
    switch (actionType) {
      case ActionType.EDIT:
        break;
      case ActionType.CREATE:
        // 新增模式下若只有一個品牌並且為"其他"則預設為手動選品，並且無法選擇智能選品
        if (
          brandOptions.some(
            ({ value }) => value === '99999999999999999' || value === '1',
          ) &&
          brandOptions.length === 1
        ) {
          setFieldValue('basic.selectPattern', 'MANUAL');
          if (values?.keywords.length === 0) {
            setFieldValue('basic.bidMode', BidMode.MANUAL);
            setFieldValue('basic.roasOption.roasStrategy', null);
            setFieldValue('basic.roasOption.roas', null);
          }
          return;
        }

        if (defaultForm.basic.selectPattern === null) {
          setFieldValue('basic.selectPattern', 'AUTO');
          return;
        }
        break;
      case ActionType.COPY:
        // 新增模式下若只有一個品牌並且為"其他"則預設為手動選品，並且無法選擇智能選品
        if (
          brandOptions.some(
            ({ value }) => value === '99999999999999999' || value === '1',
          ) &&
          brandOptions.length === 1
        ) {
          setFieldValue('basic.selectPattern', 'MANUAL');
          if (values?.keywords.length === 0) {
            setFieldValue('basic.bidMode', BidMode.MANUAL);
            setFieldValue('basic.roasOption.roasStrategy', null);
            setFieldValue('basic.roasOption.roas', null);
          }
          return;
        } else if (defaultForm.basic.selectPattern === null) {
          setFieldValue('basic.selectPattern', 'AUTO');
          return;
        }
        break;
      default:
        break;
    }
  }, [actionType, brandOptions, values?.keywords]);

  useEffect(() => {
    if (
      (actionType === ActionType.EDIT || actionType === ActionType.COPY) &&
      !_.isEmpty(filters) &&
      defaultForm.basic.bidMode === BidMode.AUTO
    ) {
      fetchProductsToGetRoas(
        defaultForm.basic.selectPattern === SelectPattern.AUTO
          ? selectedAutoProducts.map((p) => p.productId)
          : selectedProducts.map((p) => p.productId),
      );
    }
  }, [filters, defaultForm.basic.bidMode, defaultForm.basic.SelectPattern]);

  // 取得活動期間天數
  useEffect(() => {
    const startDate = moment(values?.basic.startDate);
    const endDate = moment(values?.basic.endDate);
    const defaultStartDate = moment(defaultForm?.basic.startDate);
    const defaultEndDate = moment(defaultForm?.basic.endDate);
    // 需要 +1 是因為當 startDate & endDate 都是同一天時 moment.js 會判定 diff 為 0,
    // 但這邊業務需求會需要是1天
    const diffDays = endDate.diff(startDate, 'days') + 1;
    setDiffDays(diffDays);
    // 編輯模式下日預算模式有異動開始與結束日期才執行 handleRunnungPatternAutoSetBudget
    if (values.basic.budgetType === BudgetType.DAILY) {
      if (
        actionType === ActionType.EDIT &&
        startDate.isSame(defaultStartDate) &&
        endDate.isSame(defaultEndDate) &&
        defaultForm.basic.runningPattern === values.basic.runningPattern &&
        defaultForm.basic.budgetType === values.basic.budgetType &&
        defaultForm.basic.dailyTargetBudget?.toString() ===
          values.basic.dailyTargetBudget?.toString()
      ) {
        setFieldValue('basic.budget', defaultForm.basic.budget);
      } else {
        handleAlwaysOnAutoSetBudget(values.basic.dailyTargetBudget);
      }
    }
  }, [
    values.basic.budgetType,
    values.basic.startDate,
    values.basic.endDate,
    values.basic.dailyTargetBudget,
  ]);

  return {
    order,
    brandOptions,
    retailerTypeOptions,
    suggestKeywords,
    diffDays,
    selectBrand,
    selectedAutoProducts,
    selectedProducts,
    handleSelectPatternChange,
    handleBrandChange,
    handleDurationChange,
    handleDateRangeChange,
    handleChangeStartDate,
    handleChangeEndDate,
    handleBudgetTypeChange,
    handleAlwaysOnBudgetChange,
    handleBidTypeChange,
    handleRoasStrategyChange,
    handleCustomRoasChange,
    handleManualKeywordsChange,
  };
};

export default useKeywordForm;
